import { SignInForm } from '../components/auth/SignInForm'
import { type ShellConfig } from '../config/shell-config'

export type SignInPageProps = {
  /** Shell configuration (provides routes and branding) */
  config: ShellConfig
  /** Override default callback URL if needed */
  defaultCallbackUrl?: string
  /** Search params from the page (e.g. callbackUrl from URL). Passed from Next.js page. */
  searchParams?: Promise<Record<string, string | string[] | undefined>>
}

function getParam(params: Record<string, string | string[] | undefined>, key: string): string {
  const raw = params?.[key]
  const value = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw[0] : undefined
  return value !== null && value !== undefined ? value : ''
}

function deriveParamError(errorParam: string): string {
  switch (errorParam) {
    case 'CredentialsSignin':
      return 'Invalid email or password. Please check your credentials and try again.'
    case 'Configuration':
      return 'There is a problem with the server configuration.'
    case 'AccessDenied':
      return 'Access denied. You do not have permission to sign in.'
    case 'Verification':
      return 'The verification token has expired or has already been used.'
    default:
      return errorParam ? 'An error occurred during authentication. Please try again.' : ''
  }
}

function normalizeCallbackUrl(raw: string, defaultCallback: string): string {
  try {
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      const target = new URL(raw)
      return target.pathname + (target.search !== null && target.search !== undefined ? target.search : '')
    }
  } catch {
    // ignore
  }
  return raw || defaultCallback
}

/**
 * SignInPage - Server component wrapper for the sign-in form.
 * Reads searchParams on the server and passes all derived values into the client SignInForm
 * so SignInForm does not need useSearchParams() during SSR (avoids useContext null in standalone/Docker).
 *
 * @example
 * ```tsx
 * // src/app/auth/signin/page.tsx
 * import { SignInPage } from '@korvo/shell'
 * import { shellConfig } from '@/config/shell-config'
 *
 * export default async function Page({ searchParams }: { searchParams: Promise<Record<string, string | string[] | undefined>> }) {
 *   return <SignInPage config={shellConfig} searchParams={searchParams} />
 * }
 * ```
 */
export async function SignInPage({
  config,
  defaultCallbackUrl,
  searchParams: searchParamsPromise,
}: SignInPageProps) {
  const defaultCallback = defaultCallbackUrl ?? config?.routes?.homepage ?? '/home'
  let callbackUrl = defaultCallback
  let paramError = ''
  let showRegisteredSuccess = false

  if (searchParamsPromise) {
    const params = await searchParamsPromise
    const rawCallback = getParam(params, 'callbackUrl') || defaultCallback
    callbackUrl = normalizeCallbackUrl(rawCallback, defaultCallback)
    const errorParam = getParam(params, 'error')
    paramError = deriveParamError(errorParam)
    showRegisteredSuccess = getParam(params, 'registered') === '1'
  }

  return (
    <SignInForm
      config={config}
      defaultCallbackUrl={defaultCallbackUrl}
      callbackUrl={callbackUrl}
      paramError={paramError}
      showRegisteredSuccess={showRegisteredSuccess}
    />
  )
}
